import { BellOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../api/client';
import type { components } from '../api/schema.gen';
import { formatDateTimeShort } from '../lib/dateFormat';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { NotificationActions } from './NotificationActions';

type Notification = components['schemas']['Notification'];

const POLL_INTERVAL_MS = 30_000;
const PANEL_LIMIT = 10;
const GAP = 6;
const PANEL_WIDTH = 320;

/**
 * Topbar bell (LLMR-in-app-notification-ticket-draft.md v1, action buttons
 * and the "view all" link added in v2): polls GET /me/notifications every
 * 30s for the unread badge, and opens a dropdown of the latest
 * {@link PANEL_LIMIT} on click. `unread_count` in that response is never
 * capped by page size, so the badge stays accurate even though the
 * dropdown only ever shows a handful — the full history at /notifications
 * is one click away via the panel's footer link.
 *
 * Opening the panel marks everything unread as read (POST
 * /me/notifications/read) — there is still no per-item mark-read route
 * (PO decision #6), so "I opened the bell" remains the only read/unread
 * signal there is; the action buttons v2 adds navigate away, they don't
 * mark anything read on their own.
 * Same portal-and-outside-click pattern as NavUserMenu, not shared into a
 * component because the positioning math (right-aligned under a topbar
 * trigger, no upward flip) is the only piece in common and duplicating
 * ~15 lines of it was simpler than generalizing two different panels'
 * content and width needs into one.
 */
export function NotificationBell() {
  const { dict } = useLanguage();
  const [notifications, setNotifications] = useState<Notification[] | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [style, setStyle] = useState<{ left: number; width: number; top: number }>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const poll = useCallback(async () => {
    try {
      const r = await api.notifications({ page_size: PANEL_LIMIT });
      setNotifications(r.data);
      setUnreadCount(r.unread_count);
      setError(null);
    } catch (e) {
      // Best-effort: a failed poll should not visibly break the topbar on
      // every page — the badge just holds its last known value and tries
      // again next interval. The panel, if open, shows the error instead.
      setError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.notifications.loadError);
    }
  }, [dict.notifications.loadError]);

  useEffect(() => {
    void poll();
    const id = setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [poll]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const edge = 8;
    const width = Math.min(PANEL_WIDTH, window.innerWidth - edge * 2);
    const left = Math.min(Math.max(edge, rect.right - width), window.innerWidth - width - edge);
    setStyle({ left, width, top: rect.bottom + GAP });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const isOutside = (target: EventTarget | null) =>
      !triggerRef.current?.contains(target as Node) && !panelRef.current?.contains(target as Node);
    const onPointerDown = (e: PointerEvent) => {
      if (isOutside(e.target)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // `scroll` doesn't bubble, but a capturing listener on window still fires
    // for it on the way down to whatever descendant actually scrolled — that
    // includes the panel's own overflow-y:auto body. Ignoring scrolls whose
    // target is inside the panel is what lets the list (and the "view all"
    // link past the fold) actually scroll instead of closing on first touch.
    const onReflow = (e: Event) => {
      if (e.type === 'scroll' && panelRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open]);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (!next || unreadCount === 0) return;

    // Optimistic: opening the bell IS the "read" action, so reflect that
    // immediately rather than waiting on the round-trip.
    const now = new Date().toISOString();
    setNotifications((prev) => prev?.map((n) => (n.read_at ? n : { ...n, read_at: now })) ?? prev);
    setUnreadCount(0);
    try {
      await api.markNotificationsRead();
    } catch {
      // Best-effort — the next 30s poll reconciles the true count either way.
    }
  }

  return (
    <div className="notification-bell">
      <button
        ref={triggerRef}
        type="button"
        className="notification-bell-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={dict.notifications.bellLabel}
        onClick={() => void toggle()}
      >
        <BellOutlined aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="notification-bell-badge" aria-hidden="true">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {open &&
        style &&
        createPortal(
          <div
            ref={panelRef}
            className="notification-bell-panel"
            role="menu"
            id={panelId}
            style={{ position: 'fixed', left: style.left, width: style.width, top: style.top }}
          >
            <div className="notification-bell-panel-header">{dict.notifications.panelHeading}</div>
            {error && <div className="notification-bell-panel-error">{error}</div>}
            {notifications !== null && notifications.length === 0 && !error && (
              <div className="notification-bell-panel-empty">{dict.notifications.empty}</div>
            )}
            {notifications?.map((n) => (
              <div key={n.notification_id} className="notification-bell-item" role="menuitem">
                <p className="notification-bell-item-message">{n.message}</p>
                <span className="notification-bell-item-time">{formatDateTimeShort(n.created_at)}</span>
                <NotificationActions type={n.type} onNavigate={() => setOpen(false)} />
              </div>
            ))}
            {notifications !== null && notifications.length > 0 && (
              <Link
                to="/notifications"
                className="panel-head-link notification-bell-panel-footer"
                onClick={() => setOpen(false)}
              >
                {dict.notifications.viewAllLink}
              </Link>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
